/**
 * Serviço de API — interesses de parceiro (leads de aquisição vindos da landing).
 */
import { apiService } from './api'

export type PartnerInterestStatus =
  | 'novo'
  | 'qualificando'
  | 'reuniao_agendada'
  | 'convite_enviado'
  | 'convertido'
  | 'perdido'

export interface PartnerInterest {
  id: number
  name: string
  email: string
  phone?: string | null
  company_name?: string | null
  doc?: string | null
  message?: string | null
  source?: string | null
  status: PartnerInterestStatus
  invite_id?: number | null
  assigned_to_id?: number | null
  last_contacted_at?: string | null
  next_follow_up_at?: string | null
  lost_reason?: string | null
  qualification_notes?: string | null
  status_changed_at?: string | null
  invite?: PartnerInterestInvite | null
  created_at: string
  updated_at?: string | null
}

export interface PartnerInterestInvite {
  id: number
  token: string
  link: string
  expires_at: string
}

export interface PartnerInterestUpdate {
  status?: PartnerInterestStatus
  qualification_notes?: string | null
  next_follow_up_at?: string | null
  lost_reason?: string | null
  assigned_to_id?: number | null
  last_contacted_at?: string | null
}

export interface GenerateInviteResponse {
  message: string
  token: string
  link: string
  expires_at: string
}

export type PartnerLeadAIAction = 'suggest_next_action' | 'draft_message' | 'summarize_lead'

export interface PartnerLeadAISuggestPayload {
  action: PartnerLeadAIAction
  objective: string
  context?: Record<string, unknown>
}

export interface PartnerLeadAISuggestion {
  summary: string
  questions: string[]
  next_action: string
  draft_message: string
}

class PartnerInterestsService {
  /** Lista os interesses de parceiro (Admin). */
  async list(): Promise<{ interests: PartnerInterest[] }> {
    return apiService.request<{ interests: PartnerInterest[] }>('/api/admin/partners/interests')
  }

  async followupsDue(): Promise<{ interests: PartnerInterest[] }> {
    return apiService.request<{ interests: PartnerInterest[] }>(
      '/api/admin/partners/interests/followups-due',
    )
  }

  async get(id: number): Promise<PartnerInterest> {
    return apiService.request<PartnerInterest>(`/api/admin/partners/interests/${id}`)
  }

  async update(id: number, payload: PartnerInterestUpdate): Promise<PartnerInterest> {
    return apiService.request<PartnerInterest>(`/api/admin/partners/interests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  }

  async suggestNextAction(
    id: number,
    payload: PartnerLeadAISuggestPayload,
  ): Promise<PartnerLeadAISuggestion> {
    return apiService.request<PartnerLeadAISuggestion>(
      `/api/admin/partners/interests/${id}/ai/suggest`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    )
  }

  /** Converte um interesse em convite de tenant (Admin). */
  async generateInvite(id: number): Promise<GenerateInviteResponse> {
    return apiService.request<GenerateInviteResponse>(
      `/api/admin/partners/interests/${id}/invite`,
      { method: 'POST' },
    )
  }
}

export const partnerInterestsService = new PartnerInterestsService()
